import { Fragment, type ReactNode } from "react";
import { Footer, Page, SiteHeader } from "../components/ui.tsx";
import { t } from "../i18n.ts";
import { LEGAL_PAGES, type Block, type LegalPage } from "../legal.ts";
import { linkHandler } from "../router.ts";

const EDGES = ["edge-pink", "edge-cyan", "edge-yellow"];
const PILLS = ["pill-pink", "pill-cyan", "pill-yellow"];

/** Platzhalter in [eckigen Klammern] gelb hervorheben (Entwurf 5a–5c). */
export function withPlaceholders(text: string): ReactNode {
  return text.split(/(\[[^\]]+\])/).map((part, i) =>
    part.startsWith("[") ? (
      <mark key={i} className="placeholder">
        {part}
      </mark>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

function renderBlock(block: Block, i: number) {
  if (typeof block === "string") return <p key={i}>{withPlaceholders(block)}</p>;
  return (
    <ul key={i}>
      {block.list.map((item) => (
        <li key={item}>{withPlaceholders(item)}</li>
      ))}
    </ul>
  );
}

/** Karten mit Nummern-Pille und wechselnder Kantenfarbe – für Rechtstexte und Regeln. */
export function ContentCards({ sections, idPrefix }: { sections: { title: string; body: Block[] }[]; idPrefix: string }) {
  return (
    <div className="content-cards">
      {sections.map((section, i) => (
        <article key={section.title} id={`${idPrefix}-${i + 1}`} className={`content-card ${EDGES[i % 3]}`}>
          <span className={`pill ${PILLS[i % 3]}`}>
            {i + 1}/{sections.length}
          </span>
          <h2>{section.title}</h2>
          {section.body.map(renderBlock)}
        </article>
      ))}
    </div>
  );
}

export function Legal({ page }: { page: LegalPage }) {
  return (
    <>
      <SiteHeader
        right={
          <a className="btn btn-outline btn-pill btn-small" href="/" onClick={linkHandler("/")}>
            ← {t.toHome}
          </a>
        }
      />
      <Page>
        <h1 className="title-banner">{page.title}</h1>
        {page.asOf && (
          <p className="muted" style={{ marginTop: -8 }}>
            {t.legal.asOf}: {withPlaceholders(page.asOf)}
          </p>
        )}
        <div className="legal">
          <nav className="legal-nav" aria-label={t.legal.imprint}>
            <div className="legal-tabs">
              {LEGAL_PAGES.map((p) => (
                <a key={p.path} href={p.path} onClick={linkHandler(p.path)} aria-current={p.path === page.path ? "page" : undefined}>
                  {p.title}
                </a>
              ))}
            </div>
            <div>
              <div className="label" style={{ marginTop: 10 }}>
                {t.legal.onThisPage}
              </div>
              <ol className="toc">
                {page.sections.map((s, i) => (
                  <li key={s.title}>
                    <a href={`#abschnitt-${i + 1}`}>
                      <span>{i + 1}</span>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>
          <ContentCards sections={page.sections} idPrefix="abschnitt" />
        </div>
      </Page>
      <Footer />
    </>
  );
}
